import {
  AttachFileOutlined,
  InfoOutlined,
  ScienceOutlined,
  StraightenOutlined,
} from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { getFlowTestById, updateFlowTest } from "../../api/flowTests/FlowTest";
import TabFiles from "../../components/Files/TabFiles";
import FlowTestInfo from "../../components/FlowTest/FlowTestInfo";
import InterpretationsTab from "../../components/FlowTest/Tabs/InterpretationsTab";
import MeasurementsTab from "../../components/FlowTest/Tabs/MeasurementsTab";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useAccountPermissions } from "../../hooks/Permissions/useAccountPermissions";
import { useEntityAccessPermissions } from "../../hooks/Permissions/useEntityAccessPermissions";
import { RootState } from "../../store";
import { setLoading, setTab } from "../../store/UI";
import { FlowTestResponse, UpdateFlowTestRequest } from "../../types/model/flowTest";
import { UIStore } from "../../types/store";

export default function DetailsFlowTest() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const location = useLocation();

  const { flowTestId } = useParams<{ flowTestId: string }>();

  const tabState = useSelector((state: { ui: UIStore }) => state.ui.tabState);
  const [tabIndex, setTabIndex] = useState(tabState[location.pathname] || 0);
  const [flowTestData, setFlowTestData] = useState<FlowTestResponse>();

  const { canEditData } = useAccountPermissions();
  const {
    canEditEntity,
    isLoading: isLoadingEntityPermissions,
  } = useEntityAccessPermissions();

  const canEdit =
    canEditData &&
    !isLoadingEntityPermissions &&
    flowTestData?.entityId !== undefined &&
    canEditEntity(flowTestData.entityId);

  const [open, setOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const loading = useSelector((state: RootState) => state.ui.loading);

  const handleDrawerToggle = () => {
    setIsTransitioning(true);
    setOpen(!open);
  };

  const handleTransitionEnd = () => {
    setIsTransitioning(false);
  };

  const handleTabChange = (
    _event: React.SyntheticEvent | React.MouseEvent,
    newValue: number,
  ) => {
    setTabIndex(newValue);
    dispatch(setTab({ page: location.pathname, tab: newValue }));
  };

  const handleFlowTestUpdate = async (values: UpdateFlowTestRequest) => {
    try {
      await updateFlowTest(Number.parseInt(flowTestId!), values);

      const updatedData = await getFlowTestById(Number.parseInt(flowTestId!));
      setFlowTestData(updatedData);
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    if (!flowTestId) return;
    (async () => {
      dispatch(setLoading(true));
      try {
        const data = await getFlowTestById(Number.parseInt(flowTestId));
        setFlowTestData(data);
      } catch (error) {
        handleError(error);
      } finally {
        dispatch(setLoading(false));
      }
    })();
  }, [flowTestId]);

  const drawerItems = [
    { text: t("flow-tests.tabs.general"), icon: <InfoOutlined />, index: 0 },
    {
      text: t("flow-tests.tabs.measurements"),
      icon: <StraightenOutlined />,
      index: 1,
    },
    {
      text: t("flow-tests.tabs.interpretations"),
      icon: <ScienceOutlined />,
      index: 2,
    },
    {
      text: t("flow-tests.tabs.files"),
      icon: <AttachFileOutlined />,
      index: 3,
    },
  ];

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Toolbar>
        <IconButton
          color="inherit"
          onClick={handleDrawerToggle}
          edge="start"
          sx={{ marginRight: 2 }}
        >
          {open ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
        {flowTestData && (
          <Box
            sx={{
              flex: "1 1 100%",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h4">{t("flow-tests.label")}</Typography>

            {flowTestData.captureName && (
              <Chip
                label={`${t("flow-tests.table.capture-name")}: ${flowTestData.captureName}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}

            {flowTestData.entityDesignation && (
              <Chip
                label={`${t("flow-tests.table.entity-desig")}: ${flowTestData.entityDesignation}`}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
          </Box>
        )}

        <Tooltip title={t("generic.back")}>
          <Button
            onClick={() => navigate(-1)}
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            <ArrowBackIcon />
          </Button>
        </Tooltip>
      </Toolbar>

      <Box sx={{ display: "flex" }}>
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            width: open ? 240 : (theme) => `calc(${theme.spacing(7)} + 1px)`,
            transition: (theme) =>
              theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: open
                  ? theme.transitions.duration.enteringScreen
                  : theme.transitions.duration.leavingScreen,
              }),
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              position: "relative",
              whiteSpace: "nowrap",
              boxSizing: "border-box",
              width: "inherit",
              overflowX: "hidden",
              border: "none",
              zIndex: "auto",
            },
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          <List component="nav">
            {drawerItems.map((item) => (
              <ListItem key={item.index} disablePadding>
                <Tooltip title={open ? "" : item.text} placement="left">
                  <ListItemButton
                    selected={tabIndex === item.index}
                    onClick={(e) => handleTabChange(e, item.index)}
                    sx={{
                      minHeight: 48,
                      justifyContent: "initial",
                      px: 2.5,
                      alignItems: "center",
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 3,
                        justifyContent: "center",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        opacity: open ? 1 : 0,
                        transition: (theme) =>
                          theme.transitions.create("opacity"),
                        whiteSpace:
                          open && !isTransitioning ? "normal" : "nowrap",
                        overflow: "hidden",
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </Drawer>

        <Box sx={{ flexGrow: 1, p: 3 }}>
          <Fade in={tabIndex === 0} timeout={300} unmountOnExit>
            <Box>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                tabIndex === 0 &&
                flowTestData && (
                  <FlowTestInfo
                    flowTestData={flowTestData}
                    onSubmit={handleFlowTestUpdate}
                    canEdit={canEdit}
                  />
                )
              )}
            </Box>
          </Fade>

          <Fade in={tabIndex === 1} timeout={300} unmountOnExit>
            <Box>
              {tabIndex === 1 && flowTestId && flowTestData && (
                <MeasurementsTab 
                  flowTestId={flowTestId} 
                  captureId={flowTestData.captureId} 
                  entityId={flowTestData.entityId}
                  handleError={handleError}
                  canEdit={canEdit}
                />
              )}
            </Box>
          </Fade>

          <Fade in={tabIndex === 2} timeout={300} unmountOnExit>
            <Box>
              {tabIndex === 2 && flowTestId && flowTestData && (
                <InterpretationsTab
                  flowTestId={flowTestId}
                  captureId={flowTestData.captureId}
                  entityId={flowTestData.entityId}
                  handleError={handleError}
                  canEdit={canEdit}  
                />
              )}
            </Box>
          </Fade>

          <Fade in={tabIndex === 3} timeout={300} unmountOnExit>
            <Box>
              {tabIndex === 3 && flowTestData && (
                <TabFiles
                  entityId={flowTestData.entityId}
                  captureId={flowTestData.captureId}
                  flowTestId={Number.parseInt(flowTestId!)}
                  context="flowTest"
                />
              )}
            </Box>
          </Fade>
        </Box>
      </Box>
    </Paper>
  );
}